import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { isAdProvider, type AdProvider } from "@app/shared";

/**
 * Which platform the dashboard is looking at. "all" is a real position, not an
 * absence — it is what every screen showed before there was more than one
 * platform, and it stays the default.
 */
export type ProviderFilter = AdProvider | "all";

const KEY = "moloco_tracker_provider";

const ProviderContext = createContext<{
  provider: ProviderFilter;
  setProvider: (value: ProviderFilter) => void;
  /** Undefined when "all", which is what the API treats as unfiltered. */
  scope: AdProvider | undefined;
}>({ provider: "all", setProvider: () => {}, scope: undefined });

function read(): ProviderFilter {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "all" || (saved && isAdProvider(saved))) return saved;
  } catch {
    /* private browsing */
  }
  return "all";
}

export function ProviderFilterProvider({ children }: { children: ReactNode }) {
  const [provider, setState] = useState<ProviderFilter>(read);

  const setProvider = useCallback((value: ProviderFilter) => {
    setState(value);
    try {
      localStorage.setItem(KEY, value);
    } catch {
      /* private browsing */
    }
  }, []);

  const value = useMemo(
    () => ({ provider, setProvider, scope: provider === "all" ? undefined : provider }),
    [provider, setProvider],
  );

  return <ProviderContext.Provider value={value}>{children}</ProviderContext.Provider>;
}

export const useProviderFilter = () => useContext(ProviderContext);

/**
 * The filter as a react-query key fragment. Every query that narrows by
 * platform must include this, or switching would show the previous platform's
 * cached answer under the new label.
 */
export const useProviderKey = () => useProviderFilter().provider;
